import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Sparkles, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supportChat, supportHistory, resendDownload } from "@/lib/api";
import { toast } from "sonner";

const getSession = () => {
  let s = localStorage.getItem("pt_support_sid");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    localStorage.setItem("pt_support_sid", s);
  }
  return s;
};

const SUGGESTIONS = [
  "What do the guides cost?",
  "How do I download after paying?",
  "Do you accept promo codes?",
  "How do refunds work?",
];

export const SupportChat = () => {
  const sessionId = useRef(getSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const scroller = useRef(null);

  const doResend = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail)) {
      toast.error("Enter the email you used");
      return;
    }
    setResending(true);
    try {
      const res = await resendDownload(resendEmail);
      if (res.ok) {
        toast.success(res.message);
        setShowResend(false);
        setResendEmail("");
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Couldn't resend right now. Please try again.");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    supportHistory(sessionId.current)
      .then((m) => {
        if (m?.length) setMessages(m);
        else
          setMessages([
            { role: "assistant", content: "Hi, I'm Aria 👋 — how can I help with PivoT ransition today? Ask me about the guides, pricing, downloads, promo codes or refunds." },
          ]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { reply } = await supportChat(sessionId.current, msg);
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please email legal@pivotransition.ch and we'll help." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-3xl bg-[#121118] border border-white/10 overflow-hidden flex flex-col h-[560px]" data-testid="support-chat">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[var(--neon-cyan)]/15 flex items-center justify-center">
          <Sparkles size={16} className="text-[var(--neon-cyan)]" />
        </div>
        <div className="flex-1">
          <p className="font-future text-sm">Aria · Support assistant</p>
          <p className="text-[11px] text-white/40 font-mono2">Usually replies instantly</p>
        </div>
        <button
          data-testid="resend-toggle"
          onClick={() => setShowResend((v) => !v)}
          className="flex items-center gap-1.5 label-caps text-[11px] text-white/60 hover:text-white transition-colors"
        >
          <Download size={13} /> Resend link
        </button>
      </div>

      {showResend && (
        <div className="px-5 py-4 border-b border-white/10 bg-white/[0.02] flex gap-2" data-testid="resend-box">
          <Input
            data-testid="resend-email-input"
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="Your purchase / signup email"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11 flex-1"
          />
          <button
            data-testid="resend-send-btn"
            onClick={doResend}
            disabled={resending}
            className="label-caps px-4 rounded-lg bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {resending ? <Loader2 size={14} className="animate-spin" /> : "Resend"}
          </button>
        </div>
      )}

      <div ref={scroller} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[var(--neon-cyan)] text-black" : "bg-white/[0.06] text-white/85 border border-white/10"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-3 bg-white/[0.06] border border-white/10">
              <Loader2 size={16} className="animate-spin text-white/60" />
            </div>
          </div>
        )}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid="chat-suggestion"
                onClick={() => send(s)}
                className="text-xs px-3 py-2 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/40 transition-colors duration-300"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="p-4 border-t border-white/10 flex gap-2"
      >
        <Input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12"
        />
        <motion.button
          whileTap={{ scale: 0.94 }}
          type="submit"
          data-testid="chat-send-btn"
          disabled={loading}
          className="h-12 w-12 rounded-xl bg-[var(--neon-cyan)] text-black flex items-center justify-center hover:bg-white transition-colors duration-300 disabled:opacity-60 shrink-0"
        >
          <Send size={16} />
        </motion.button>
      </form>
    </div>
  );
};
